/**
 * Initializes the language selector dropdown.
 *
 * - Navigates to the same page with an updated `locale` query param on change.
 * - On browsers without CSS `field-sizing` (Firefox, Chrome < 123), sizes the
 *   <select> to its selected value so the chevron hugs the label and the footer
 *   group stays centered — matching what `field-sizing: content` gives modern
 *   browsers from CSS alone.
 *
 * root/selector let a caller scope this to an injected subtree — e.g. the
 * legal documents viewer's own selector, injected into the in-app drawer
 * alongside the host layout's own #language-selector, needs its own id to
 * avoid a duplicate-id collision (see legal_documents_drawer.js).
 */
export const initLanguageSelector = (root = document, selector = '#language-selector') => {
  const select = /** @type {HTMLSelectElement | null} */ (
    root.querySelector(selector)
  );
  if (!select) return;

  select.addEventListener('change', ({ target }) => {
    const url = new URL(location.href);
    url.searchParams.set('locale', /** @type {HTMLSelectElement} */ (target).value);
    location.href = url.href;
  });

  const supportsFieldSizing = globalThis.CSS?.supports?.('field-sizing', 'content') ?? false;
  if (supportsFieldSizing) return;

  // Render an off-screen copy holding only the selected option and let the
  // browser size it natively (value + chevron + padding), then match that
  // width. Cloning keeps this box-model agnostic — no manual padding math.
  const fitWidthToSelectedValue = () => {
    const option = select.options[select.selectedIndex];
    if (!option) return;

    const probe = /** @type {HTMLSelectElement} */ (select.cloneNode(false));
    probe.removeAttribute('id');
    probe.append(new Option(option.text));
    Object.assign(probe.style, { position: 'absolute', visibility: 'hidden', width: 'auto' });
    select.after(probe);
    select.style.width = `${Math.ceil(probe.getBoundingClientRect().width)}px`;
    probe.remove();
  };

  fitWidthToSelectedValue();
  // Web fonts can finish loading after first paint; re-measure so a fallback
  // font's width does not leave the control mis-sized.
  void (async () => {
    await document.fonts?.ready;
    fitWidthToSelectedValue();
  })();
};
